import { useCallback, useEffect, useRef, useState } from "react";

import { faker } from "@faker-js/faker";
import type { Meta, StoryObj } from "@storybook/react";

import {
  fakeAllSMTDailyEntriesModalProps,
  fakeSMTSingleDayEntriesAccordionProps,
} from "@/__mocks__/SMT";
import { fakeUserTab } from "@/__mocks__/user";
import type { AllSMTDailyEntriesModalProps } from "@/ui/components/smt/ViewSMTEntries/AllSMTDailyEntriesModal";
import AllSMTDailyEntriesModal from "@/ui/components/smt/ViewSMTEntries/AllSMTDailyEntriesModal";
import ModalLayout from "@/ui/layout/ModalLayout";

type StoryProps = AllSMTDailyEntriesModalProps;

const fakeCustomUserTabs = () =>
  faker.helpers.multiple(
    () => ({
      ...fakeUserTab(),
      badgeCount: 0,
      userName: faker.person.firstName(),
    }),
    {
      count: { max: 8, min: 2 },
    },
  );

const StoryMeta: Meta<StoryProps> = {
  title: "Components/SMT/All Daily Entries Accordion",
  component: AllSMTDailyEntriesModal,
  argTypes: {
    accordionPropsList: { control: "object" },
    userTabs: { control: "object" },
  },
  args: {
    accordionPropsList: faker.helpers.multiple(
      () => fakeSMTSingleDayEntriesAccordionProps(),
      { count: { max: 20, min: 2 } },
    ),
    userTabs: fakeCustomUserTabs(),
  },
  // eslint-disable-next-line no-restricted-syntax -- non-arrow function used here as React FC needed for use of state hook
  render: function Render(args) {
    const { accordionPropsList, userTabs } = args;
    // allow both re-generation of fake props, and use of storybook controls
    const [props, setProps] = useState<AllSMTDailyEntriesModalProps>({
      ...fakeAllSMTDailyEntriesModalProps(),
      accordionPropsList,
      userTabs,
    });
    const onUserChange = useCallback(() => {
      setProps((current) => ({
        ...current,
        ...getFakePropsRef.current(),
        // keep current user tabs (since just changed users), but re-gen fake props
        userTabs,
      }));
    }, [userTabs]);
    const getFakeProps = useCallback(
      () => ({
        ...fakeAllSMTDailyEntriesModalProps(),
        onUserChange,
        userTabs: fakeCustomUserTabs(),
      }),
      [onUserChange],
    );
    const getFakePropsRef = useRef(getFakeProps);
    useEffect(() => {
      getFakePropsRef.current = getFakeProps;
    }, [getFakeProps]);

    useEffect(() => {
      setProps((current) => ({
        ...current,
        ...getFakePropsRef.current(),
        accordionPropsList,
        userTabs,
      }));
    }, [accordionPropsList, userTabs]);

    return (
      <ModalLayout>
        <AllSMTDailyEntriesModal {...props} />
      </ModalLayout>
    );
  },
};

type Story = StoryObj<StoryProps>;

const Default: Story = {};

export { Default };
export default StoryMeta;
